import {
  Directive,
  ViewContainerRef,
  OnDestroy,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Renderer2,
  ElementRef,
  reflectComponentType,
  Injector,
  inject,
  TemplateRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ReplaceableConfigService } from '../../services/replaceable-config.service';
import { ReplaceableComponentBase } from './replaceable-component-base';
import { DisableReplacementDirective } from './disable-replacement.directive';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';


@Directive({
  selector: '[replaceable]',
  standalone: true
})
export class ReplaceableDirective implements OnDestroy {
  replaceComponent: any;

  componentRef: ComponentRef<any> | null = null;

  protected outputSubscriptions: Subscription[] = [];

  protected observedAttributes = ['hidden'];

  protected viewContainerRef = inject(ViewContainerRef);
  protected environmentInjector = inject(EnvironmentInjector);
  protected renderer = inject(Renderer2);
  protected elementRef = inject(ElementRef);
  protected injector = inject(Injector);
  protected originalComponentInstance = inject(ReplaceableComponentBase, { self: true });
  protected templateRef = inject(TemplateRef, { optional: true });
  protected mutationObserver: MutationObserver | null = null;
  protected disableReplacement: DisableReplacementDirective | null = inject(DisableReplacementDirective, { optional: true });
  protected replaceableConfig = inject(ReplaceableConfigService);



  constructor() {
    if (!this.disableReplacement) {
      this.replaceComponent = this.replaceableConfig.getReplaceable(this.originalComponentInstance.componentDescriptors);
      if (this.replaceComponent && this.originalComponentInstance) {
        this.originalComponentInstance.replacementComponentInitialized = true;
        this.createComponentInstance();
        this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'none');
      } else {
        this.originalComponentInstance['instance'] = this.originalComponentInstance;
        this.originalComponentInstance.replacementComponentInitialized = false;
      }
    } else {
      this.originalComponentInstance['instance'] = this.originalComponentInstance;
      this.originalComponentInstance.replacementComponentInitialized = false;
    }
  }




  protected createComponentInstance(): void {
    this.viewContainerRef.clear();
    this.outputSubscriptions.forEach(sub => sub.unsubscribe());
    this.outputSubscriptions = [];
    if (this.originalComponentInstance['dialogRef']) {
       this.createDialogComponent();
    } else if (this.originalComponentInstance['bottomSheetRef']) {
       this.createBottomSheetComponent();
    } else {
       this.createComponent();
    }

    this.viewContainerRef.insert(this.componentRef.hostView);
    this.originalComponentInstance['instance'] = this.componentRef.instance;
    this.copyInputsAndOutputs();
    this.copyStylesAndClasses();
    this.observeAttributes();
  }


  createBottomSheetComponent(): void {
    const customInjector = Injector.create({
      providers: [
        {
          provide: MatBottomSheetRef,
          useValue: this.originalComponentInstance['bottomSheetRef'],
        },
        {
          provide: MAT_BOTTOM_SHEET_DATA,
          useValue: this.originalComponentInstance['data'],
        },
      ],
      parent: this.injector,
    });
    this.createComponent(customInjector);
  }

  createDialogComponent(): void {
    const customInjector = Injector.create({
      providers: [
        {
          provide: MatDialogRef,
          useValue: this.originalComponentInstance['dialogRef'],
        },
        {
          provide: MAT_DIALOG_DATA,
          useValue: this.originalComponentInstance['data'],
        },
      ],
      parent: this.injector,
    });
    this.createComponent(customInjector);
  }

  createComponent(injector = null): void {
    this.componentRef = createComponent(this.replaceComponent, {
      environmentInjector: this.environmentInjector,
      ...(injector ? {elementInjector: injector}: {})

   });
  }

  protected observeAttributes(): void {
    if (this.componentRef && this.elementRef) {
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            this.copyStylesAndClasses();
          }
          this.observedAttributes.forEach(attr => {
          if (mutation.attributeName === attr) {
            const attrValue = this.elementRef.nativeElement[attr];
            this.renderer.setProperty(
              this.componentRef.location.nativeElement,
              attr,
              attrValue
            );
          }
          });
        });
      });

      this.mutationObserver.observe(this.elementRef.nativeElement, {
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }
  }



  protected copyStylesAndClasses(): void {
    if (!this.elementRef) {
      return;
    }

    const source = this.elementRef instanceof ElementRef ? this.elementRef.nativeElement : this.elementRef;
    const target = this.componentRef.location.nativeElement;

    if (!source || !target) {
      return;
    }

   // Copy inline styles
   if (source.style) {
    for (let i = 0; i < source.style.length; i++) {
      const property = source.style[i];
      if (property !== 'display') {
        this.renderer.setStyle(target, property, source.style.getPropertyValue(property));
      }
    }
  }

    // Copy classes
    const classes = source.classList;
    for (let i = 0; i < classes.length; i++) {
      this.renderer.addClass(target, classes[i]);
    }
  }
  


  protected copyInputsAndOutputs(): void {
    if (this.componentRef && this.originalComponentInstance) {
      const replacementInstance = this.componentRef.instance;
      const originalComponentType = this.originalComponentInstance.constructor;
      const originalMetadata = reflectComponentType(originalComponentType as any);
      const replacementComponentType = replacementInstance.constructor;
      const replacementMetadata = reflectComponentType(replacementComponentType as any);

      if (!originalMetadata) return;

  
        // Copy initial input values
        originalMetadata.inputs.forEach(({ propName }) => {
          if (propName in replacementInstance) {
            replacementInstance[propName] = this.originalComponentInstance[propName];
          }
         });


          // Wrap input properties with setters if ngOnChanges not available
          originalMetadata.inputs.forEach(({ propName }) => {
              this.wrapInputProperty(propName, replacementInstance, replacementMetadata);
          });

          // Copy Outputs
        originalMetadata.outputs.forEach(({ propName }) => {
          const output = replacementInstance[propName];
          if (output?.subscribe) {
            const sub = output.subscribe((event: any) => {
              const originalOutput = this.originalComponentInstance[propName];
              if (originalOutput?.emit) {
                originalOutput.emit(event);
              }
            });
            this.outputSubscriptions.push(sub);
          }
        });


    }
  }

  

  protected wrapInputProperty(propName: string, replacementInstance: any, replacementMetadata: any): void {
    let value = this.originalComponentInstance[propName];
    const isReplacementInput = replacementMetadata.inputs.map(i => i.propName).includes(propName);
    const descriptor = Object.getOwnPropertyDescriptor(
        this.originalComponentInstance,
        propName
    );

    if (descriptor?.set) {
        // const originalSet = descriptor.set;
        descriptor.set = (newValue: any) => {
            //Only set the value in the replacement instance.
            if (isReplacementInput) {
               replacementInstance[propName] = newValue;
            }
        };
        Object.defineProperty(
            this.originalComponentInstance,
            propName,
            descriptor
        );
    } else {
        Object.defineProperty(
            this.originalComponentInstance,
            propName,
            {
                get: () => value,
                set: (newValue: any) => {
                  if (isReplacementInput) {
                    value = newValue;
                    replacementInstance[propName] = newValue;
                  }
                },
                enumerable: true,
                configurable: true,
            }
        );
    }
    if (isReplacementInput) {
        replacementInstance[propName] = value;
    }
}


  ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.outputSubscriptions.forEach(sub => sub.unsubscribe());
  }
}